import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Typo } from '@/components/ui/typography';
import { formatCurrency } from '@/lib/accounting';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import { ChevronUp, Minus, Plus, ShoppingCart, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

interface CartItem {
  id: string;
  name: string;
  siteName: string;
  date: string;
  quantity: number;
  price: number;
  total: number;
}

interface MobileCartBottomProps {
  className?: string;
  minPrice?: number | null;
}

const formatVND = (amount: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

export function MobileCartBottom({ className = '', minPrice }: MobileCartBottomProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  // Mock cart data - this should be replaced with actual cart state
  useEffect(() => {
    const mockCartItems: CartItem[] = [
      {
        id: '1',
        name: 'Vé VinWonders Phú Quốc',
        siteName: 'VinWonders Phú Quốc',
        date: '2024-12-25',
        quantity: 2,
        price: 650000,
        total: 1300000,
      },
    ];

    // Only set mock data if cart is empty (for development purposes)
    if (cartItems.length === 0) {
      // setCartItems(mockCartItems);
    }
  }, []);

  const cartTotal = cartItems.reduce((sum, item) => sum + item.total, 0);
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleUpdateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }

    setCartItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, quantity: newQuantity, total: item.price * newQuantity } : item)));
  };

  const handleRemoveItem = (itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // Empty cart: show price anchor CTA
  if (cartItems.length === 0) {
    const handleScrollToPackages = () => {
      document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
      <></>
    );
  }

  return (
    <div className={cn('lg:hidden', className)}>
      {/* Bottom pinned cart bar */}
      <div className="fixed right-0 bottom-0 left-0 z-40 border-t bg-white shadow-lg">
        <div className="container px-4 py-3">
          <div className="flex items-center justify-between">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" className="flex h-auto items-center space-x-2 p-0">
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <ShoppingCart className="h-6 w-6" />
                      {totalItems > 0 && (
                        <span className="bg-primary absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white">
                          {totalItems > 99 ? '99+' : totalItems}
                        </span>
                      )}
                    </div>
                    <div className="text-left">
                      <Typo variant="small" className="font-medium">
                        {totalItems} sản phẩm
                      </Typo>
                      <Typo variant="muted" className="text-xs">
                        Xem giỏ hàng
                      </Typo>
                    </div>
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  </div>
                </Button>
              </SheetTrigger>

              <SheetContent side="bottom" className="h-[70vh] rounded-t-xl">
                <SheetHeader className="mb-4 border-b pb-4">
                  <SheetTitle className="flex items-center justify-between">
                    <Typo variant="h5">Giỏ hàng ({totalItems})</Typo>
                    <Typo variant="h5" className="text-primary font-bold">
                      {formatCurrency(cartTotal)}
                    </Typo>
                  </SheetTitle>
                </SheetHeader>

                {/* Cart Items */}
                <div className="mb-4 flex-1 space-y-4 overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="rounded-lg border p-4">
                      <div className="mb-3 flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <Typo variant="small" className="mb-1 font-medium text-gray-900">
                            {item.name}
                          </Typo>
                          <Typo variant="muted">{item.siteName}</Typo>
                          <Typo variant="muted">Ngày: {item.date}</Typo>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveItem(item.id)}
                          className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center rounded border">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            className="h-8 w-8 p-0"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Typo variant="small" className="min-w-[40px] px-3 py-1 text-center font-medium">
                            {item.quantity}
                          </Typo>
                          <Button size="sm" variant="ghost" onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)} className="h-8 w-8 p-0">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="text-right">
                          <Typo variant="small" className="font-medium text-gray-900">
                            {formatCurrency(item.total)}
                          </Typo>
                          <Typo variant="muted" className="text-xs">
                            {formatCurrency(item.price)} x {item.quantity}
                          </Typo>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom actions */}
                <div className="space-y-3 border-t pt-4">

                  <div className="grid grid-cols-2 gap-3">
                    <Button asChild variant="outline" onClick={() => setIsOpen(false)}>
                      <Link href="/cart">Chi tiết</Link>
                    </Button>

                    <Button asChild onClick={() => setIsOpen(false)}>
                      <Link href="/checkout">Thanh toán</Link>
                    </Button>
                  </div>

                  <div className="text-center">
                    <Typo variant="muted" className="text-xs">
                      🔒 Thanh toán an toàn • Hỗ trợ hủy vé miễn phí
                    </Typo>
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Quick checkout button */}
            <Button asChild>
              <Link href="/checkout" className="flex items-center space-x-2">
                <Typo variant="small">Thanh toán</Typo>
                <Typo variant="small" className="font-bold">
                  {formatCurrency(cartTotal)}
                </Typo>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
